import { z } from "zod";

// Kept byte-for-byte compatible in shape with Peer UX's WhitelabelConfig contract.
const CSS_COLOR_RE = /^(#[0-9A-Fa-f]{3,8}|[a-z]+(\(.+\))?)$/;
const PX_OR_REM_RE = /^\d+(\.\d+)?(px|rem)$/;

const cssColourSchema = z.string().refine(
  (value) => {
    const trimmed = value.trim();
    return trimmed.length > 0 && CSS_COLOR_RE.test(trimmed);
  },
  { message: "Use a CSS colour value." },
);

const pxOrRemSchema = z.string().refine(
  (value) => {
    const trimmed = value.trim();
    return PX_OR_REM_RE.test(trimmed) || trimmed === "0";
  },
  { message: "Use a px or rem value." },
);

const colourScaleSchema = z.object({
  DEFAULT: cssColourSchema,
  foreground: cssColourSchema,
});

const peerThemeColoursSchema = z.object({
  background: cssColourSchema,
  foreground: cssColourSchema,
  card: colourScaleSchema,
  popover: colourScaleSchema,
  primary: colourScaleSchema,
  secondary: colourScaleSchema,
  muted: colourScaleSchema,
  accent: colourScaleSchema,
  destructive: z.object({
    DEFAULT: cssColourSchema,
    foreground: cssColourSchema,
    solid: cssColourSchema,
  }),
  border: cssColourSchema,
  input: cssColourSchema,
  ring: cssColourSchema,
  chart: z.object({
    "1": cssColourSchema,
    "2": cssColourSchema,
    "3": cssColourSchema,
    "4": cssColourSchema,
    "5": cssColourSchema,
  }),
  sidebar: z.object({
    DEFAULT: cssColourSchema,
    foreground: cssColourSchema,
    primary: colourScaleSchema,
    accent: colourScaleSchema,
    border: cssColourSchema,
    ring: cssColourSchema,
  }),
  success: colourScaleSchema,
  warning: colourScaleSchema,
  info: colourScaleSchema,
});

const peerThemeRadiusSchema = z.object({
  DEFAULT: pxOrRemSchema,
  button: z.string().optional(),
  input: z.string().optional(),
  card: z.string().optional(),
  dialog: z.string().optional(),
  badge: z.string().optional(),
  full: z.string().optional(),
});

const peerThemeShadowsSchema = z.object({
  color: z.string(),
  opacity: z.number(),
  "2xs": z.string(),
  xs: z.string(),
  sm: z.string(),
  DEFAULT: z.string().min(1),
  md: z.string(),
  lg: z.string(),
  xl: z.string(),
  "2xl": z.string(),
});

const peerThemeTypographySchema = z.object({
  fontSans: z.string().min(1),
  fontSerif: z.string().min(1),
  fontMono: z.string().min(1),
  baseFontSize: z.string().optional(),
  lineHeight: z.string().optional(),
});

/** The shared white-label theme interchange format. */
export const peerWhitelabelThemeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  colors: peerThemeColoursSchema,
  radius: peerThemeRadiusSchema,
  shadows: peerThemeShadowsSchema,
  typography: peerThemeTypographySchema,
});

// Accept older theme exports and upgrade them at the boundary.
const legacyColourPairSchema = z.object({
  surface: cssColourSchema,
  foreground: cssColourSchema,
});

const legacyPeerStartThemeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  colors: z.object({
    background: cssColourSchema,
    foreground: cssColourSchema,
    card: legacyColourPairSchema,
    popover: legacyColourPairSchema,
    primary: legacyColourPairSchema,
    secondary: legacyColourPairSchema,
    accent: legacyColourPairSchema,
    muted: legacyColourPairSchema,
    destructive: legacyColourPairSchema,
    warning: legacyColourPairSchema,
    success: legacyColourPairSchema,
    info: legacyColourPairSchema,
    border: cssColourSchema,
    input: cssColourSchema,
    ring: cssColourSchema,
    sidebar: z.object({
      surface: cssColourSchema,
      foreground: cssColourSchema,
      primary: cssColourSchema,
      primaryForeground: cssColourSchema,
      accent: cssColourSchema,
      accentForeground: cssColourSchema,
      border: cssColourSchema,
      ring: cssColourSchema,
    }),
  }),
  radius: pxOrRemSchema,
  shadows: z.object({ xs: z.string(), sm: z.string(), md: z.string(), lg: z.string() }),
  typography: z.object({
    fontSans: z.string().min(1),
    baseFontSize: z.string(),
    lineHeight: z.string(),
  }),
});

function upgradeLegacyTheme(theme: z.infer<typeof legacyPeerStartThemeSchema>) {
  const pair = (value: z.infer<typeof legacyColourPairSchema>) => ({
    DEFAULT: value.surface,
    foreground: value.foreground,
  });
  return {
    id: theme.id,
    name: theme.name,
    colors: {
      background: theme.colors.background,
      foreground: theme.colors.foreground,
      card: pair(theme.colors.card),
      popover: pair(theme.colors.popover),
      primary: pair(theme.colors.primary),
      secondary: pair(theme.colors.secondary),
      muted: pair(theme.colors.muted),
      accent: pair(theme.colors.accent),
      destructive: {
        ...pair(theme.colors.destructive),
        solid: theme.colors.destructive.foreground,
      },
      border: theme.colors.border,
      input: theme.colors.input,
      ring: theme.colors.ring,
      chart: {
        "1": theme.colors.primary.surface,
        "2": theme.colors.accent.surface,
        "3": theme.colors.secondary.surface,
        "4": theme.colors.info.foreground,
        "5": theme.colors.success.foreground,
      },
      sidebar: {
        DEFAULT: theme.colors.sidebar.surface,
        foreground: theme.colors.sidebar.foreground,
        primary: {
          DEFAULT: theme.colors.sidebar.primary,
          foreground: theme.colors.sidebar.primaryForeground,
        },
        accent: {
          DEFAULT: theme.colors.sidebar.accent,
          foreground: theme.colors.sidebar.accentForeground,
        },
        border: theme.colors.sidebar.border,
        ring: theme.colors.sidebar.ring,
      },
      success: pair(theme.colors.success),
      warning: pair(theme.colors.warning),
      info: pair(theme.colors.info),
    },
    radius: { DEFAULT: theme.radius },
    shadows: {
      color: theme.colors.primary.surface,
      opacity: 0.1,
      "2xs": theme.shadows.xs,
      xs: theme.shadows.xs,
      sm: theme.shadows.sm,
      DEFAULT: theme.shadows.md,
      md: theme.shadows.md,
      lg: theme.shadows.lg,
      xl: theme.shadows.lg,
      "2xl": theme.shadows.lg,
    },
    typography: {
      fontSans: theme.typography.fontSans,
      fontSerif: "Georgia, serif",
      fontMono: "ui-monospace, monospace",
      baseFontSize: theme.typography.baseFontSize,
      lineHeight: theme.typography.lineHeight,
    },
  };
}

export const peerStartThemeSchema = z.preprocess((value) => {
  const legacy = legacyPeerStartThemeSchema.safeParse(value);
  return legacy.success ? upgradeLegacyTheme(legacy.data) : value;
}, peerWhitelabelThemeSchema);

export const builtInThemeIdSchema = z.enum(["peer-start", "peer-start-warm"]);

export const organisationThemeSchema = z.object({
  organisationId: z.string().min(1),
  baseThemeId: builtInThemeIdSchema,
  theme: peerStartThemeSchema,
  custom: z.boolean(),
  revision: z.number().int().positive(),
});

export const organisationThemeUpdateSchema = z.object({
  baseThemeId: builtInThemeIdSchema,
  theme: peerStartThemeSchema,
  expectedRevision: z.number().int().positive(),
});

export type BuiltInThemeId = z.infer<typeof builtInThemeIdSchema>;
export type PeerWhitelabelTheme = z.infer<typeof peerWhitelabelThemeSchema>;
export type PeerStartTheme = z.output<typeof peerStartThemeSchema>;
export type OrganisationTheme = z.infer<typeof organisationThemeSchema>;
export type OrganisationThemeUpdate = z.infer<typeof organisationThemeUpdateSchema>;
